import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { getFriends } from "../../functions/user";
import { Search } from "../../svg";
import "./style.css";

const DEFAULT_AVATAR = "/images/default_pic.png";

function getDisplayName(friend) {
  return (
    friend.name ||
    `${friend.first_name || ""} ${friend.last_name || ""}`.trim() ||
    "Unknown"
  );
}

function getInitials(friend) {
  const first = (friend.first_name || "").trim();
  const last = (friend.last_name || "").trim();
  if (first || last) {
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || "?";
  }
  const name = getDisplayName(friend);
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
  }
  return (name.charAt(0) || "?").toUpperCase();
}

function FriendAvatar({ friend }) {
  const [failed, setFailed] = useState(false);
  const hasPicture = Boolean(friend.picture) && !failed;

  if (!hasPicture) {
    return (
      <span className="tag_friend_avatar tag_friend_avatar_placeholder" aria-hidden="true">
        {getInitials(friend)}
      </span>
    );
  }

  return (
    <img
      src={friend.picture}
      alt=""
      className="tag_friend_avatar"
      onError={() => setFailed(true)}
    />
  );
}

export default function FriendsListModal({ onClose, onSelectFriends, selectedFriends = [] }) {
  const [friends, setFriends] = useState([]);
  const [filteredFriends, setFilteredFriends] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(selectedFriends);
  const { user } = useSelector((state) => ({ ...state }));

  const loadFriends = useCallback(async () => {
    try {
      setLoading(true);
      const friendsList = await getFriends(user.token);

      if (Array.isArray(friendsList)) {
        setFriends(friendsList);
        setFilteredFriends(friendsList);
      } else {
        console.error("Error loading friends:", friendsList);
        setFriends([]);
        setFilteredFriends([]);
      }
    } catch (error) {
      console.error("Error loading friends:", error);
      setFriends([]);
      setFilteredFriends([]);
    } finally {
      setLoading(false);
    }
  }, [user.token]);

  useEffect(() => {
    loadFriends();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadFriends]);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  useEffect(() => {
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      const filtered = friends.filter((friend) => {
        const fullName = `${friend.first_name || ""} ${friend.last_name || ""}`.toLowerCase();
        const name = (friend.name || "").toLowerCase();
        const username = (friend.username || "").toLowerCase();
        return fullName.includes(q) || name.includes(q) || username.includes(q);
      });
      setFilteredFriends(filtered);
    } else {
      setFilteredFriends(friends);
    }
  }, [searchQuery, friends]);

  const handleFriendToggle = (friendId) => {
    setSelected((prev) => {
      const isSelected = prev.includes(friendId);
      if (isSelected) {
        return prev.filter((id) => id !== friendId);
      }
      return [...prev, friendId];
    });
  };

  const handleDone = () => {
    if (!selected.length) return;
    const selectedFriendsData = friends.filter((friend) =>
      selected.includes(friend._id)
    );
    onSelectFriends(selectedFriendsData);
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  const modal = (
    <div
      className="tag_friends_modal"
      role="dialog"
      aria-modal="true"
      aria-label="Tag Friends"
      onClick={handleCancel}
    >
      <div
        className="tag_friends_modal_content"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="tag_friends_top">
          <div className="tag_friends_modal_header">
            <h3>Tag Friends</h3>
            <button
              type="button"
              className="close_btn"
              onClick={handleCancel}
              aria-label="Close tag friends"
            >
              <i className="exit_icon"></i>
            </button>
          </div>

          <div className="tag_friends_search">
            <div className="search_wrap">
              <Search color="#8a8d91" />
              <input
                type="text"
                placeholder="Search friends..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoComplete="off"
              />
            </div>
          </div>
        </div>

        <div className="tag_friends_content">
          {loading ? (
            <div className="loading_friends">
              <div className="spinner">
                <div className="bounce1"></div>
                <div className="bounce2"></div>
                <div className="bounce3"></div>
              </div>
              <span>Loading friends...</span>
            </div>
          ) : filteredFriends.length > 0 ? (
            <div className="tag_friends_list" role="listbox" aria-multiselectable="true">
              {filteredFriends.map((friend) => {
                const isSelected = selected.includes(friend._id);
                const displayName = getDisplayName(friend);

                return (
                  <label
                    key={friend._id}
                    className={`tag_friend_row${isSelected ? " selected" : ""}`}
                    role="option"
                    aria-selected={isSelected}
                  >
                    <FriendAvatar friend={friend} />
                    <span className="tag_friend_meta">
                      <span className="tag_friend_name">{displayName}</span>
                      {friend.username ? (
                        <span className="tag_friend_username">@{friend.username}</span>
                      ) : null}
                    </span>
                    <input
                      type="checkbox"
                      className="tag_friend_checkbox_input"
                      checked={isSelected}
                      onChange={() => handleFriendToggle(friend._id)}
                      aria-label={`Tag ${displayName}`}
                    />
                    <span className="tag_friend_checkbox" aria-hidden="true">
                      {isSelected ? (
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none">
                          <path
                            d="M5 12.5l4.5 4.5L19 7.5"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      ) : null}
                    </span>
                  </label>
                );
              })}
            </div>
          ) : (
            <div className="no_friends">
              <img src={DEFAULT_AVATAR} alt="" className="no_friends_icon" />
              <span>No friends found</span>
              {searchQuery ? (
                <p>Try a different name or username</p>
              ) : (
                <p>Add friends to tag them in your post</p>
              )}
            </div>
          )}
        </div>

        <div className="tag_friends_modal_footer">
          <button type="button" className="cancel_btn" onClick={handleCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="done_btn"
            onClick={handleDone}
            disabled={selected.length === 0}
          >
            Done{selected.length > 0 ? ` (${selected.length})` : ""}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
